import { FC, useSyncExternalStore } from 'react';

import { Box, Button, HStack, Text } from '@chakra-ui/react';

import type {
  ConnieVoiceCallStatus,
  MicLevelSource,
} from '@/hooks/useConnieVoiceCall';
import type { ConnieVoiceMode } from '@/services/connieVoice';

export type VoiceCallControl = {
  /** Who answers, in labels. Connie by default. */
  agentName?: string;
  status: ConnieVoiceCallStatus;
  mode: ConnieVoiceMode;
  /** Mic level, 0–1, read by the meter alone. */
  level: MicLevelSource;
  /** What the user is saying, before the turn ends. */
  caption: string;
  onStart: () => void;
  onStop: () => void;
  onEndTurn: () => void;
};

const statusLabel = (status: ConnieVoiceCallStatus, agentName: string) => {
  switch (status) {
    case 'connecting':
      return 'Connecting…';
    case 'listening':
    case 'hearing':
      return 'Listening';
    case 'thinking':
      return `${agentName} is answering`;
    case 'off':
      return '';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
};

const METER_BARS = [0.55, 0.85, 1, 0.85, 0.55];

type VoiceTone = 'listening' | 'answering' | 'connecting';

const voiceTone = (status: ConnieVoiceCallStatus): VoiceTone => {
  switch (status) {
    case 'listening':
    case 'hearing':
      return 'listening';
    case 'thinking':
      return 'answering';
    case 'connecting':
    case 'off':
      return 'connecting';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
};

/** Subscribes to the mic level itself, so only the bars repaint as you talk. */
const MicMeter: FC<{ level: MicLevelSource; active: boolean }> = ({
  level,
  active,
}) => {
  const value = useSyncExternalStore(level.subscribe, level.get, () => 0);
  return (
    <HStack spacing='3px' h='16px' align='flex-end' flexShrink={0} aria-hidden>
      {METER_BARS.map((weight, index) => (
        <Box
          key={index}
          w='2px'
          borderRadius='full'
          bg={active ? 'green.400' : 'whiteAlpha.300'}
          h={`${Math.max(20, Math.round((active ? value : 0) * weight * 100))}%`}
          transition='height 90ms linear'
          sx={{
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        />
      ))}
    </HStack>
  );
};

/** Steady mark for a live listen. The bars carry the sound; this carries the state. */
const LiveMark: FC = () => (
  <Box
    position='relative'
    boxSize='8px'
    flexShrink={0}
    data-cy='connie-voice-live'
  >
    <Box
      position='absolute'
      top='-4px'
      right='-4px'
      bottom='-4px'
      left='-4px'
      borderRadius='full'
      bg='alternative.green-20'
    />
    <Box
      position='relative'
      boxSize='8px'
      borderRadius='full'
      bg='green.400'
      data-cy='connie-voice-live-core'
    />
  </Box>
);

/** Calm in-input strip for a live call: state, mic level and live caption. */
export const VoiceCallBar: FC<{ call: VoiceCallControl }> = ({ call }) => {
  const tone = voiceTone(call.status);
  const listening = tone === 'listening';
  const showSend = call.mode === 'push_to_talk' && listening;
  // FSI ignores the mic while the agent answers, so say so rather than seem to listen.
  const hint =
    call.status === 'thinking'
      ? 'Wait for her to finish'
      : call.mode === 'push_to_talk'
        ? 'Tap send when you finish'
        : 'Pause to send';

  return (
    <HStack
      position='absolute'
      insetY={0}
      left={0}
      right={0}
      px={4}
      pr='136px'
      spacing={3}
      bg='gray.800'
      borderRadius='inherit'
      zIndex={2}
      data-cy='connie-voice-call'
      data-status={call.status}
      data-tone={tone}
      // The field stays dark. Listening is a light from the mic, not a filled
      // status bar. The work beam around the composer still means she is working.
      sx={{
        transitionProperty: 'background-image, box-shadow',
        transitionDuration: '150ms',
        transitionTimingFunction: 'ease-out',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        ...(listening
          ? {
              backgroundColor: 'var(--chakra-colors-gray-800)',
              backgroundImage:
                'linear-gradient(180deg, var(--chakra-colors-white-50), transparent 46%), radial-gradient(130% 180% at 0% 80%, color-mix(in srgb, var(--chakra-colors-green-400) 42%, transparent) 0%, transparent 58%)',
              boxShadow:
                'inset 0 1px 0 var(--chakra-colors-white-200), inset 0 -10px 22px color-mix(in srgb, var(--chakra-colors-green-400) 28%, transparent)',
            }
          : {}),
      }}
    >
      {listening ? <LiveMark /> : null}
      <MicMeter level={call.level} active={listening} />
      <Text
        color='white.900'
        fontSize='sm'
        fontWeight='medium'
        flexShrink={0}
        aria-live='polite'
        data-cy='connie-voice-status'
      >
        {statusLabel(call.status, call.agentName ?? 'Connie')}
      </Text>
      <Text
        color='whiteAlpha.600'
        fontSize='sm'
        noOfLines={1}
        minW={0}
        data-cy='connie-voice-caption'
      >
        {call.caption || hint}
      </Text>
      {showSend && (
        <Button
          size='xs'
          variant='ghost'
          color='whiteAlpha.800'
          flexShrink={0}
          ml='auto'
          onClick={call.onEndTurn}
        >
          Send
        </Button>
      )}
    </HStack>
  );
};
